'use client'

import { useEffect, useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdAdd,
    MdEdit,
    MdDelete,
    MdDescription,
    MdClose,
    MdCheck,
    MdPrint,
    MdSearch,
    MdPerson,
    MdDraw,
    MdVerified,
    MdDownload,
    MdPictureAsPdf,
} from 'react-icons/md'
import { getFileUrl } from '@/lib/utils/file-url'
import { useAuth } from '@/store/hooks'
import {
    MedicalCertificate,
    MedicalCertificateType,
    IcdCode,
    CreateCertificateData,
    UpdateCertificateData,
    getCertificatesByClient,
    createCertificate,
    updateCertificate,
    deleteCertificate,
    printCertificatePdf,
    searchIcdCodes,
    CERTIFICATE_TYPE_LABELS,
} from '@/lib/api/prontuario/prontuario-api'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useProntuarioData } from './prontuario-context'
import ConfirmModal from '@/components/confirm-modal'
import { SignDocumentModal } from '@/components/signature/SignDocumentModal'
import {
    TemplateCategory,
} from '@/lib/api/templates/template-api'
import { stripHtml } from '@/lib/utils/strip-html'
import SaveAsTemplateModal from '@/components/medical/save-as-template-modal'
import TemplatePicker from '@/components/medical/template-picker'

interface CertificateFormData {
    type: MedicalCertificateType
    startDate: string
    endDate: string
    daysOff: string
    includeIcd: boolean
    icdCodeId: number | null
    purpose: string
    content: string
    professionalId: string
}

const defaultFormData: CertificateFormData = {
    type: MedicalCertificateType.SICK_LEAVE,
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: '',
    daysOff: '',
    includeIcd: false,
    icdCodeId: null,
    purpose: '',
    content: '',
    professionalId: '',
}

const certificateTemplates: Record<MedicalCertificateType, string> = {
    [MedicalCertificateType.SICK_LEAVE]: `Atesto para os devidos fins que o(a) paciente acima identificado(a) esteve sob meus cuidados médicos e necessita de afastamento de suas atividades laborais por [DIAS] dias, a partir de [DATA_INICIO].`,
    [MedicalCertificateType.FITNESS]: `Atesto para os devidos fins que o(a) paciente acima identificado(a) encontra-se em boas condições de saúde física, apto(a) para [FINALIDADE].`,
    [MedicalCertificateType.ACCOMPANIMENT]: `Atesto para os devidos fins que o(a) paciente acima identificado(a) necessita de acompanhante durante [PERIODO].`,
    [MedicalCertificateType.SCHOOL]: `Atesto para os devidos fins escolares que o(a) paciente acima identificado(a) esteve sob meus cuidados médicos na data de [DATA].`,
    [MedicalCertificateType.SPORTS]: `Atesto para os devidos fins que o(a) paciente acima identificado(a) encontra-se apto(a) para prática de atividades físicas e esportivas.`,
    [MedicalCertificateType.TRAVEL]: `Atesto para os devidos fins que o(a) paciente acima identificado(a) encontra-se em condições de saúde para realizar viagem.`,
    [MedicalCertificateType.WORK_RETURN]: `Atesto para os devidos fins que o(a) paciente acima identificado(a) encontra-se apto(a) para retornar às suas atividades laborais a partir de [DATA].`,
    [MedicalCertificateType.CERTIFICATE_OTHER]: '',
}

export default function AtestadosPage() {
    const { client, clientId } = useProntuarioData()
    const { user, isProfessional } = useAuth()

    const [certificates, setCertificates] = useState<MedicalCertificate[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [printing, setPrinting] = useState<number | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [formData, setFormData] =
        useState<CertificateFormData>(defaultFormData)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [certificateToDelete, setCertificateToDelete] = useState<
        number | null
    >(null)
    const [showSignModal, setShowSignModal] = useState(false)
    /** Veio de um modelo? Se sim, não convidamos a salvar de novo. */
    const [usedTemplate, setUsedTemplate] = useState(false)
    /** Atestado recém-criado, à espera de virar modelo. */
    const [pendingTemplate, setPendingTemplate] = useState<{
        content: string
        professionalId: number | null
    } | null>(null)
    const [certificateToSign, setCertificateToSign] =
        useState<MedicalCertificate | null>(null)

    // ICD Search
    const [icdSearchQuery, setIcdSearchQuery] = useState('')
    const [icdSearchResults, setIcdSearchResults] = useState<IcdCode[]>([])
    const [selectedIcd, setSelectedIcd] = useState<IcdCode | null>(null)

    useEffect(() => {
        if (clientId && user?.companyId) {
            loadCertificates()
        }
    }, [clientId, user?.companyId])

    const loadCertificates = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const response = await getCertificatesByClient(
                parseInt(clientId, 10)
            )
            if (response.success) {
                setCertificates(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar atestados')
            }
        } catch (error) {
            toast.error('Erro ao carregar atestados')
        } finally {
            setLoading(false)
        }
    }

    const handleIcdSearch = useCallback(async (query: string) => {
        if (query.length < 2) {
            setIcdSearchResults([])
            return
        }

        try {
            const response = await searchIcdCodes(query)
            if (response.success) {
                setIcdSearchResults(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao buscar CID:', error)
        }
    }, [])

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            handleIcdSearch(icdSearchQuery)
        }, 300)

        return () => clearTimeout(timeoutId)
    }, [icdSearchQuery, handleIcdSearch])

    const selectIcd = (icd: IcdCode) => {
        setSelectedIcd(icd)
        setIcdSearchQuery('')
        setIcdSearchResults([])
        setFormData(prev => ({ ...prev, icdCodeId: icd.id }))
    }

    const clearSelectedIcd = () => {
        setSelectedIcd(null)
        setFormData(prev => ({ ...prev, icdCodeId: null }))
    }

    const openModal = (certificate?: MedicalCertificate) => {
        if (certificate) {
            setEditingId(certificate.id)
            setFormData({
                type: certificate.type,
                startDate: certificate.startDate
                    ? certificate.startDate.split('T')[0]
                    : '',
                endDate: certificate.endDate
                    ? certificate.endDate.split('T')[0]
                    : '',
                daysOff: certificate.daysOff?.toString() || '',
                includeIcd: certificate.includeIcd,
                icdCodeId: certificate.icdCodeId || null,
                purpose: certificate.purpose || '',
                content: certificate.content,
                professionalId: certificate.professionalId?.toString() || '',
            })
            if (certificate.icdCode) {
                setSelectedIcd(certificate.icdCode)
            }
        } else {
            setEditingId(null)
            setFormData({
                ...defaultFormData,
                content:
                    certificateTemplates[MedicalCertificateType.SICK_LEAVE],
            })
            setSelectedIcd(null)
        }
        setModalOpen(true)
    }

    const closeModal = () => {
        setUsedTemplate(false)
        setModalOpen(false)
        setEditingId(null)
        setFormData(defaultFormData)
        setSelectedIcd(null)
        setIcdSearchQuery('')
        setIcdSearchResults([])
    }

    const handleTypeChange = (type: MedicalCertificateType) => {
        setFormData(prev => ({
            ...prev,
            type,
            content: editingId ? prev.content : certificateTemplates[type],
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.content.trim()) {
            toast.error('Informe o conteúdo do atestado')
            return
        }

        if (!isProfessional && !formData.professionalId) {
            toast.error('Selecione um profissional')
            return
        }

        if (!clientId) return

        setSaving(true)
        try {
            if (editingId) {
                const updateData: UpdateCertificateData = {
                    type: formData.type,
                    startDate: formData.startDate,
                    endDate: formData.endDate || undefined,
                    daysOff: formData.daysOff
                        ? parseInt(formData.daysOff, 10)
                        : undefined,
                    includeIcd: formData.includeIcd,
                    icdCodeId: formData.includeIcd
                        ? formData.icdCodeId || undefined
                        : undefined,
                    purpose: formData.purpose.trim() || undefined,
                    content: formData.content.trim(),
                }

                const response = await updateCertificate(editingId, updateData)
                if (response.success) {
                    toast.success('Atestado atualizado com sucesso!')
                    await loadCertificates()
                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao atualizar atestado')
                }
            } else {
                const createData: CreateCertificateData = {
                    clientId: parseInt(clientId, 10),
                    companyId: user!.companyId!,
                    professionalId: isProfessional
                        ? user!.professionalId!
                        : parseInt(formData.professionalId, 10),
                    type: formData.type,
                    startDate: formData.startDate,
                    endDate: formData.endDate || undefined,
                    daysOff: formData.daysOff
                        ? parseInt(formData.daysOff, 10)
                        : undefined,
                    includeIcd: formData.includeIcd,
                    icdCodeId: formData.includeIcd
                        ? formData.icdCodeId || undefined
                        : undefined,
                    purpose: formData.purpose.trim() || undefined,
                    content: formData.content.trim(),
                }

                const response = await createCertificate(createData)
                if (response.success) {
                    toast.success('Atestado criado com sucesso!')
                    // O texto escrito à mão é o que vale virar modelo. Guardado
                    // ANTES do `closeModal`, que limpa o formulário.
                    if (!usedTemplate && createData.content.trim()) {
                        setPendingTemplate({
                            content: createData.content,
                            professionalId: createData.professionalId,
                        })
                    }
                    await loadCertificates()
                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao criar atestado')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar atestado')
        } finally {
            setSaving(false)
        }
    }

    const handlePrint = async (id: number) => {
        setPrinting(id)
        try {
            await printCertificatePdf(id)
            toast.success('PDF gerado com sucesso!')
            await loadCertificates()
        } catch (error) {
            toast.error('Erro ao gerar PDF para impressão')
        } finally {
            setPrinting(null)
        }
    }

    const handleDelete = (id: number) => {
        setCertificateToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!certificateToDelete) return

        try {
            const response = await deleteCertificate(certificateToDelete)
            if (response.success) {
                toast.success('Atestado excluído com sucesso!')
                await loadCertificates()
            } else {
                toast.error(response.error || 'Erro ao excluir atestado')
            }
        } catch (error) {
            toast.error('Erro ao excluir atestado')
        } finally {
            setShowDeleteModal(false)
            setCertificateToDelete(null)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando atestados...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">
                        Atestados
                    </h2>
                    <p className="text-sm text-base-content/60">
                        Gerencie os atestados médicos do paciente
                    </p>
                </div>
                <button
                    onClick={() => openModal()}
                    className="btn btn-secondary gap-2"
                >
                    <MdAdd className="text-lg" />
                    Novo Atestado
                </button>
            </div>

            {/* Certificates List */}
            {certificates.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdDescription className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">
                        Nenhum atestado registrado
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {certificates.map(certificate => (
                        <div
                            key={certificate.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden"
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="p-2 bg-secondary/10 rounded-lg">
                                                <MdDescription className="text-xl text-secondary" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-base-content">
                                                    {
                                                        CERTIFICATE_TYPE_LABELS[
                                                            certificate.type
                                                        ]
                                                    }
                                                </h3>
                                                <p className="text-sm text-base-content/60">
                                                    Emitido em:{' '}
                                                    {format(
                                                        new Date(
                                                            certificate.issueDate
                                                        ),
                                                        'dd/MM/yyyy',
                                                        { locale: ptBR }
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {certificate.daysOff && (
                                                <span className="badge badge-info">
                                                    {certificate.daysOff} dia(s)
                                                    de afastamento
                                                </span>
                                            )}
                                            {certificate.icdCode &&
                                                certificate.includeIcd && (
                                                    <span className="badge badge-ghost">
                                                        CID:{' '}
                                                        {
                                                            certificate.icdCode
                                                                .code
                                                        }
                                                    </span>
                                                )}
                                            {certificate.signedAt && (
                                                <span className="badge badge-success gap-1">
                                                    <MdVerified className="text-sm" />
                                                    Assinado
                                                </span>
                                            )}
                                        </div>

                                        <p className="text-sm text-base-content/70 line-clamp-2 mb-3">
                                            {certificate.content}
                                        </p>

                                        <div className="flex items-center gap-4 text-xs text-base-content/50">
                                            <span>
                                                Período:{' '}
                                                {format(
                                                    new Date(
                                                        certificate.startDate
                                                    ),
                                                    'dd/MM/yyyy',
                                                    { locale: ptBR }
                                                )}
                                                {certificate.endDate &&
                                                    ` - ${format(new Date(certificate.endDate), 'dd/MM/yyyy', { locale: ptBR })}`}
                                            </span>
                                            {certificate.printCount > 0 && (
                                                <span>
                                                    Impressões:{' '}
                                                    {certificate.printCount}
                                                </span>
                                            )}
                                        </div>

                                        {certificate.professional && (
                                            <div className="flex items-center gap-2 text-xs text-base-content/50 mt-2">
                                                <MdPerson className="text-sm" />
                                                <span>
                                                    {
                                                        certificate.professional
                                                            .name
                                                    }
                                                    {certificate.professional
                                                        .registrationNumber && (
                                                        <>
                                                            {' '}
                                                            -{' '}
                                                            {
                                                                certificate
                                                                    .professional
                                                                    .councilName
                                                            }{' '}
                                                            {
                                                                certificate
                                                                    .professional
                                                                    .registrationNumber
                                                            }
                                                        </>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-1">
                                        {!certificate.signedAt && (
                                            <button
                                                onClick={() => {
                                                    setCertificateToSign(
                                                        certificate
                                                    )
                                                    setShowSignModal(true)
                                                }}
                                                className="btn btn-ghost btn-sm btn-square text-success"
                                                title="Exportar PDF com assinatura digital"
                                            >
                                                <MdPictureAsPdf className="text-lg" />
                                            </button>
                                        )}
                                        {certificate.signedPdfUrl && (
                                            <a
                                                href={getFileUrl(
                                                    certificate.signedPdfUrl
                                                )}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-ghost btn-sm btn-square"
                                                title="Baixar PDF assinado"
                                            >
                                                <MdDownload className="text-lg" />
                                            </a>
                                        )}
                                        <button
                                            onClick={() =>
                                                handlePrint(certificate.id)
                                            }
                                            className="btn btn-ghost btn-sm btn-square"
                                            title="Imprimir PDF"
                                            disabled={
                                                printing === certificate.id
                                            }
                                        >
                                            {printing === certificate.id ? (
                                                <span className="loading loading-spinner loading-sm"></span>
                                            ) : (
                                                <MdPrint className="text-lg" />
                                            )}
                                        </button>
                                        <button
                                            onClick={() =>
                                                openModal(certificate)
                                            }
                                            className="btn btn-ghost btn-sm btn-square"
                                            title="Editar"
                                        >
                                            <MdEdit className="text-lg" />
                                        </button>
                                        <button
                                            onClick={() =>
                                                handleDelete(certificate.id)
                                            }
                                            className="btn btn-ghost btn-sm btn-square text-error"
                                            title="Excluir"
                                        >
                                            <MdDelete className="text-lg" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {modalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">
                                {editingId
                                    ? 'Editar Atestado'
                                    : 'Novo Atestado'}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="btn btn-ghost btn-sm btn-square"
                            >
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {!isProfessional && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Profissional{' '}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </span>
                                    </label>
                                    <ProfessionalSearchSelector
                                        value={
                                            formData.professionalId
                                                ? Number(
                                                      formData.professionalId
                                                  )
                                                : null
                                        }
                                        onChange={id =>
                                            setFormData(prev => ({
                                                ...prev,
                                                professionalId: id
                                                    ? String(id)
                                                    : '',
                                            }))
                                        }
                                        label=""
                                        placeholder="Selecione..."
                                        disabled={!!editingId}
                                    />
                                </div>
                            )}

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Tipo de Atestado{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.type}
                                    onChange={e =>
                                        handleTypeChange(
                                            e.target
                                                .value as MedicalCertificateType
                                        )
                                    }
                                >
                                    {Object.entries(
                                        CERTIFICATE_TYPE_LABELS
                                    ).map(([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Data Início{' '}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </span>
                                    </label>
                                    <input
                                        type="date"
                                        className="input input-bordered w-full"
                                        value={formData.startDate}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                startDate: e.target.value,
                                            }))
                                        }
                                        required
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Data Fim
                                        </span>
                                    </label>
                                    <input
                                        type="date"
                                        className="input input-bordered w-full"
                                        value={formData.endDate}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                endDate: e.target.value,
                                            }))
                                        }
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Dias de Afastamento
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        className="input input-bordered w-full"
                                        placeholder="0"
                                        value={formData.daysOff}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                daysOff: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Finalidade
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Para que será utilizado o atestado..."
                                    value={formData.purpose}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            purpose: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            {/* Include ICD */}
                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={formData.includeIcd}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                includeIcd: e.target.checked,
                                            }))
                                        }
                                    />
                                    <span className="label-text font-medium">
                                        Incluir CID no atestado
                                    </span>
                                </label>
                            </div>

                            {formData.includeIcd && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Código CID
                                        </span>
                                    </label>
                                    {selectedIcd ? (
                                        <div className="flex items-center gap-2 p-3 bg-base-200 rounded-lg">
                                            <span className="flex-1">
                                                <strong>
                                                    {selectedIcd.code}
                                                </strong>{' '}
                                                - {selectedIcd.description}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={clearSelectedIcd}
                                                className="btn btn-ghost btn-sm btn-square"
                                            >
                                                <MdClose className="text-lg" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <input
                                                type="text"
                                                className="input input-bordered w-full pr-10"
                                                placeholder="Buscar código CID..."
                                                value={icdSearchQuery}
                                                onChange={e =>
                                                    setIcdSearchQuery(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <MdSearch className="absolute z-10 right-3 top-1/2 -translate-y-1/2 text-base-content/50" />
                                            {icdSearchResults.length > 0 && (
                                                <div className="absolute z-10 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                    {icdSearchResults.map(
                                                        icd => (
                                                            <button
                                                                key={icd.id}
                                                                type="button"
                                                                onClick={() =>
                                                                    selectIcd(
                                                                        icd
                                                                    )
                                                                }
                                                                className="w-full px-4 py-2 text-left hover:bg-base-200 text-sm"
                                                            >
                                                                <strong>
                                                                    {icd.code}
                                                                </strong>{' '}
                                                                -{' '}
                                                                {
                                                                    icd.description
                                                                }
                                                            </button>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Modelos: o texto que este médico sempre usa. Aplicar
                                SUBSTITUI o conteúdo — diferente do pedido de exames,
                                onde somar faz sentido; dois textos de atestado
                                emendados não formam um atestado. */}
                            {!editingId && clientId && (
                                <TemplatePicker
                                    category={TemplateCategory.CERTIFICATE}
                                    clientId={parseInt(clientId, 10)}
                                    icdCodeId={selectedIcd?.id ?? null}
                                    label="Usar um modelo"
                                    onApply={applied => {
                                        setUsedTemplate(true)
                                        setFormData(prev => ({
                                            ...prev,
                                            // O campo é textarea, não editor rico:
                                            // o HTML do modelo entra como texto.
                                            content: stripHtml(
                                                applied.content ?? ''
                                            ),
                                        }))
                                    }}
                                />
                            )}


                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Conteúdo do Atestado{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Texto do atestado..."
                                    rows={6}
                                    value={formData.content}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            content: e.target.value,
                                        }))
                                    }
                                    required
                                />
                            </div>

                            <div className="modal-action">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="btn btn-ghost"
                                    disabled={saving}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-secondary gap-2"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm"></span>
                                            Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <MdCheck className="text-lg" />
                                            Salvar
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                    <div className="modal-backdrop" onClick={closeModal}></div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setCertificateToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir atestado"
                message="Tem certeza que deseja excluir este atestado?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />

            {pendingTemplate && (
                <SaveAsTemplateModal
                    isOpen
                    onClose={() => setPendingTemplate(null)}
                    category={TemplateCategory.CERTIFICATE}
                    content={pendingTemplate.content}
                    professionalId={pendingTemplate.professionalId}
                    // O seletor recarrega sozinho ao reabrir o formulário.
                    onSaved={() => setPendingTemplate(null)}
                />
            )}

            {certificateToSign && (
                <SignDocumentModal
                    isOpen={showSignModal}
                    onClose={() => {
                        setShowSignModal(false)
                        setCertificateToSign(null)
                    }}
                    documentType="MEDICAL_CERTIFICATE"
                    documentId={certificateToSign.id}
                    documentTitle={`Atestado - ${CERTIFICATE_TYPE_LABELS[certificateToSign.type]}`}
                    onSigned={() => loadCertificates()}
                />
            )}
        </div>
    )
}
